import { useEffect, useMemo, useRef, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { addJalaliMonths } from "@/lib/format";
import JalaliDatePicker from "@/components/JalaliDatePicker";
import { formatDecimal, formatNumber, formatPercent, persianDigits } from "@/features/reports/lib/format";
import { useBalance } from "../hooks/useBalance";
import { ChartLegend, chartLegendStyle } from "../components/chartTheme";
import { fa } from "../i18n/fa";
import "../traceability.css";

const PRODUCTS = ["beef", "lamb", "chicken", "sunflower_oil"];

const SERIES = [
  { key: "supply_kg", color: "#16836b" },
  { key: "sales_kg", color: "#d97706" },
  { key: "stock_kg", color: "#2563eb" },
  { key: "sale_price_per_kg", color: "#a855f7", axis: "price" },
  { key: "retail_price_per_kg", color: "#e11d48", axis: "price" },
];

const formatKg = (value) => `${formatDecimal(value, 0)} ${fa.balance.kg}`;
const formatPrice = (value) => `${formatDecimal(value, 0)} ${fa.balance.toman}`;

function defaultDateRange(data) {
  if (!data?.buckets?.length || !data.to_date) return null;
  const earliest = addJalaliMonths(data.to_date, -12);
  if (!earliest) return null;
  return {
    from: earliest,
    to: data.to_date,
  };
}

function getBalanceState(bucket) {
  const supply = Number(bucket.supply_kg) || 0;
  const sales = Number(bucket.sales_kg) || 0;
  if (supply <= 0 && sales <= 0) return "unavailable";
  if (supply <= 0) return "shortage";
  if (sales <= 0) return "surplus";

  const index = bucket.balance_index == null
    ? (supply - sales) / supply
    : Number(bucket.balance_index);
  if (!Number.isFinite(index)) return "balanced";
  if (index > 0.1) return "surplus";
  if (index < -0.1) return "shortage";
  return "balanced";
}

function getBalanceIndex(bucket) {
  const supply = Number(bucket.supply_kg) || 0;
  const sales = Number(bucket.sales_kg) || 0;
  if (supply <= 0 && sales > 0) return -1;
  if (supply > 0 && sales <= 0) return 1;
  const index = bucket.balance_index == null
    ? (supply > 0 ? (supply - sales) / supply : 0)
    : Number(bucket.balance_index);
  return Number.isFinite(index) ? Math.max(-1, Math.min(1, index)) : 0;
}

function getVerdict(bucket, state) {
  const supply = Number(bucket.supply_kg) || 0;
  const sales = Number(bucket.sales_kg) || 0;
  if (state === "surplus" && sales === 0) {
    return fa.balance.verdicts.noSales(formatDecimal(supply, 0));
  }
  if (state === "shortage" && supply === 0) {
    return fa.balance.verdicts.noSupply(formatDecimal(sales, 0));
  }

  const stockDirection = fa.balance.verdicts.stockDirections[bucket.stock_direction]
    ?? fa.balance.verdicts.stockDirections.steady;
  if (state === "balanced") {
    return fa.balance.verdicts.balanced(stockDirection);
  }

  const basis = state === "surplus" ? sales : supply;
  const difference = Math.abs(supply - sales);
  const percent = basis > 0 ? formatDecimal((difference / basis) * 100, 0) : "۰";
  return fa.balance.verdicts[state](percent, stockDirection);
}

export default function BalancePage() {
  const [product, setProduct] = useState("beef");
  const [granularity, setGranularity] = useState("month");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [dateRangeTouched, setDateRangeTouched] = useState(false);
  const [defaultRangeInitialized, setDefaultRangeInitialized] = useState(false);
  const { data, loading, error } = useBalance({
    product,
    granularity,
    from_date: fromDate || undefined,
    to_date: toDate || undefined,
  });

  if (!defaultRangeInitialized && !dateRangeTouched && data) {
    const range = defaultDateRange(data);
    setDefaultRangeInitialized(true);
    if (range) {
      setFromDate(range.from);
      setToDate(range.to);
    }
  }

  const chartData = useMemo(
    () => data?.buckets.map((bucket) => ({
      ...bucket,
      label: persianDigits(bucket.label),
    })) ?? [],
    [data],
  );
  const current = data?.buckets.at(-1);
  const balanceState = current ? getBalanceState(current) : "unavailable";
  const balanceIndex = current ? getBalanceIndex(current) : 0;
  const angle = balanceIndex * 12;
  const directionClass = current?.price_direction ?? "unavailable";

  return (
    <main className="trace tr-balance" dir="rtl">
      <div className="tr-top">
        <div className="tr-top-text">
          <p className="tr-eyebrow">{fa.page.eyebrow}</p>
          <h1>{fa.balance.title}</h1>
          <div className="tr-sub">{fa.balance.description}</div>
        </div>
        <div className="tr-import-filters">
          <label>
            <span>{fa.balance.product}</span>
            <select className="tr-select" value={product} onChange={(event) => setProduct(event.target.value)}>
              {PRODUCTS.map((item) => (
                <option key={item} value={item}>{fa.balance.products[item]}</option>
              ))}
            </select>
          </label>
          <label>
            <span>{fa.balance.granularity}</span>
            <select className="tr-select" value={granularity} onChange={(event) => setGranularity(event.target.value)}>
              <option value="week">{fa.balance.week}</option>
              <option value="month">{fa.balance.month}</option>
              <option value="year">{fa.balance.year}</option>
            </select>
          </label>
          <label>
            <span>{fa.balance.fromDate}</span>
            <JalaliDatePicker className="tr-select" value={fromDate} onChange={(value) => {
              setDateRangeTouched(true);
              setFromDate(value);
            }} aria-label="از تاریخ شمسی" rangeStart={fromDate} rangeEnd={toDate} />
          </label>
          <label>
            <span>{fa.balance.toDate}</span>
            <JalaliDatePicker className="tr-select" value={toDate} onChange={(value) => {
              setDateRangeTouched(true);
              setToDate(value);
            }} aria-label="تا تاریخ شمسی" rangeStart={fromDate} rangeEnd={toDate} />
          </label>
        </div>
      </div>

      {error && <div className="tr-card tr-error">{fa.balance.error}</div>}
      {loading && !data && <div className="tr-card tr-loading">{fa.page.loading}</div>}

      {data && (
        <>
          <section className={`tr-card tr-panel tr-balance-card state-${balanceState}`}>
            <div className="tr-balance-heading">
              <h2 className="tr-title">{fa.balance.scaleTitle}</h2>
              {current && <span className="tr-balance-period">{persianDigits(current.label)}</span>}
            </div>
            {current && balanceState !== "unavailable" ? (
              <>
                <div className={`tr-balance-visual state-${balanceState}`}>
                  <span className="tr-balance-state-badge">{fa.balance.stateBadges[balanceState]}</span>
                  <BalanceScale
                    angle={angle}
                    balanceIndex={balanceIndex}
                    supply={current.supply_kg}
                    sales={current.sales_kg}
                    label={fa.balance.scaleLabel}
                  />
                  <div className="tr-balance-scale-legend" style={chartLegendStyle}>
                    <span><i />{fa.balance.supply}</span>
                    <span><i />{fa.balance.sales}</span>
                    <span><i className="band" />{fa.balance.balancedBandLabel}</span>
                  </div>
                  <p className="tr-balance-verdict">{getVerdict(current, balanceState)}</p>
                </div>
                <dl className="tr-balance-stats">
                  <div>
                    <dt>{fa.balance.stock}</dt>
                    <dd>{formatKg(current.stock_kg)}</dd>
                  </div>
                  <div>
                    <dt>{fa.balance.factoryPrice}</dt>
                    <dd>
                      {current.sale_price_per_kg == null ? "—" : formatPrice(current.sale_price_per_kg)}
                      <span className={`tr-balance-price-direction ${directionClass}`}>
                        {fa.balance.priceDirections[directionClass]}
                      </span>
                      {current.sale_price_per_kg == null && <small>{fa.balance.priceMissing}</small>}
                    </dd>
                  </div>
                  <div>
                    <dt>{fa.balance.retailPrice}</dt>
                    <dd>
                      {current.retail_price_per_kg == null ? "—" : formatPrice(current.retail_price_per_kg)}
                      {current.retail_price_per_kg == null && <small>{fa.balance.priceMissing}</small>}
                    </dd>
                  </div>
                </dl>
              </>
            ) : (
              <div className="tr-balance-empty">
                <span className="tr-balance-state-badge">{fa.balance.stateBadges.unavailable}</span>
              </div>
            )}
          </section>

          <section className="tr-card tr-panel tr-balance-trend">
            <h2 className="tr-title">{fa.balance.trendTitle}</h2>
            <p className="tr-note">{fa.balance.trendNote}</p>
            {chartData.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 12, right: 18, left: 12, bottom: 8 }}>
                  <CartesianGrid stroke="var(--gray-light)" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tickFormatter={persianDigits} minTickGap={28} />
                  <YAxis
                    yAxisId="quantity"
                    orientation="right"
                    tickFormatter={formatNumber}
                    width={72}
                  />
                  <YAxis
                    yAxisId="price"
                    orientation="left"
                    tickFormatter={formatNumber}
                    width={76}
                  />
                  <Tooltip
                    labelFormatter={persianDigits}
                    formatter={(value, key) => [
                      value == null
                        ? "—"
                        : SERIES.find((series) => series.key === key)?.axis === "price"
                          ? formatPrice(value)
                          : formatKg(value),
                      fa.balance.series[key],
                    ]}
                  />
                  <ChartLegend formatter={(key) => fa.balance.series[key]} />
                  {SERIES.map((series) => (
                    <Line
                      key={series.key}
                      dataKey={series.key}
                      name={series.key}
                      yAxisId={series.axis ?? "quantity"}
                      stroke={series.color}
                      strokeWidth={2}
                      dot={false}
                      connectNulls
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="tr-empty"><p>{fa.balance.noData}</p></div>
            )}
          </section>
        </>
      )}
    </main>
  );
}

function BalanceScale({ angle, balanceIndex, supply, sales, label }) {
  const stageRef = useRef(null);
  const [stageWidth, setStageWidth] = useState(320);
  const stageHeight = 300;
  const centerX = stageWidth / 2;
  const pivotY = 64;
  const plateWidth = Math.min(230, stageWidth * 0.4);
  const armLength = Math.min(
    stageWidth * 0.29,
    (stageWidth - plateWidth) / 2 - 8,
  );
  const angleRadians = (angle * Math.PI) / 180;
  const textOffset = 54;
  const salesX = centerX - armLength * Math.cos(angleRadians) - textOffset * Math.sin(angleRadians);
  const salesY = pivotY - armLength * Math.sin(angleRadians) + textOffset * Math.cos(angleRadians);
  const supplyX = centerX + armLength * Math.cos(angleRadians) - textOffset * Math.sin(angleRadians);
  const supplyY = pivotY + armLength * Math.sin(angleRadians) + textOffset * Math.cos(angleRadians);
  const needleAngle = balanceIndex * 75;
  const dialCenterY = 214;
  const bandStart = pointOnGauge(centerX, dialCenterY, -7.5, 54);
  const bandEnd = pointOnGauge(centerX, dialCenterY, 7.5, 54);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return undefined;

    const observer = new ResizeObserver(([entry]) => {
      setStageWidth(Math.round(entry.contentRect.width));
    });
    observer.observe(stage);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <div className="tr-balance-stage" ref={stageRef}>
        <svg
          className="tr-balance-scale"
          viewBox={`0 0 ${stageWidth} ${stageHeight}`}
          role="img"
          aria-label={`${label}؛ ${fa.balance.sales} ${formatNumber(sales)} ${fa.balance.kg}؛ ${fa.balance.supply} ${formatNumber(supply)} ${fa.balance.kg}`}
        >
          <g
            className="tr-balance-beam"
            style={{ transform: `rotate(${angle}deg)`, transformOrigin: `${centerX}px ${pivotY}px` }}
          >
            <line x1={centerX - armLength} y1={pivotY} x2={centerX + armLength} y2={pivotY} />
            <path
              d={`M${centerX - armLength} ${pivotY} V${pivotY + 24} M${centerX + armLength} ${pivotY} V${pivotY + 24}`}
            />
            <circle cx={centerX} cy={pivotY} r="9" />
            <path
              className="tr-balance-pan"
              d={`M${centerX - armLength - plateWidth / 2} ${pivotY + 24} Q${centerX - armLength - plateWidth / 2 + 12} ${pivotY + 78} ${centerX - armLength} ${pivotY + 78} Q${centerX - armLength + plateWidth / 2 - 12} ${pivotY + 78} ${centerX - armLength + plateWidth / 2} ${pivotY + 24} Z`}
            />
            <path
              className="tr-balance-pan"
              d={`M${centerX + armLength - plateWidth / 2} ${pivotY + 24} Q${centerX + armLength - plateWidth / 2 + 12} ${pivotY + 78} ${centerX + armLength} ${pivotY + 78} Q${centerX + armLength + plateWidth / 2 - 12} ${pivotY + 78} ${centerX + armLength + plateWidth / 2} ${pivotY + 24} Z`}
            />
          </g>
          <path
            className="tr-balance-stand"
            d={`M${centerX} ${pivotY} V278 M${centerX - 30} 286 H${centerX + 30} M${centerX - 14} 278 L${centerX - 30} 286 M${centerX + 14} 278 L${centerX + 30} 286`}
          />
          <path
            className="tr-balance-pivot"
            d={`M${centerX - 14} ${pivotY + 1} L${centerX} ${pivotY - 22} L${centerX + 14} ${pivotY + 1} Z`}
          />
          <circle className="tr-balance-dial-face" cx={centerX} cy={dialCenterY} r="39" />
          <path
            className="tr-balance-dial-track"
            d={`M${centerX - 54} ${dialCenterY} A54 54 0 0 1 ${centerX + 54} ${dialCenterY}`}
          />
          <path
            className="tr-balance-dial-band"
            d={`M${bandStart.x} ${bandStart.y} A54 54 0 0 1 ${bandEnd.x} ${bandEnd.y}`}
          />
          <path
            className="tr-balance-dial-ticks"
            d={`M${centerX - 54} ${dialCenterY - 7} l8 2 M${centerX} ${dialCenterY - 54} v10 M${centerX + 54} ${dialCenterY - 7} l-8 2`}
          />
          <g
            className="tr-balance-dial-needle"
            style={{ transform: `rotate(${needleAngle}deg)`, transformOrigin: `${centerX}px ${dialCenterY}px` }}
          >
            <line x1={centerX} y1={dialCenterY} x2={centerX} y2={dialCenterY - 29} />
          </g>
          <circle className="tr-balance-dial-hub" cx={centerX} cy={dialCenterY} r="6" />
        </svg>
        <PlateText
          className="sales"
          label={fa.balance.sales}
          value={sales}
          left={salesX}
          top={salesY}
          width={plateWidth - 12}
        />
        <PlateText
          className="supply"
          label={fa.balance.supply}
          value={supply}
          left={supplyX}
          top={supplyY}
          width={plateWidth - 12}
        />
      </div>
      <div className="tr-balance-index" aria-live="polite">
        {Number(supply) > 0 && Number(sales) === 0
          ? fa.balance.noSalesIndex
          : <><bdi>{fa.balance.index}: </bdi><bdi dir="rtl">{formatPercent(balanceIndex * 100)}</bdi></>}
      </div>
    </>
  );
}

function PlateText({ className, label, value, left, top, width }) {
  const amount = Number(value) || 0;
  const isCompact = Math.abs(amount) >= 10_000;
  const isLarge = Math.abs(amount) >= 1_000_000;
  const displayValue = isLarge
    ? formatDecimal(amount / 1_000_000, 1)
    : isCompact
      ? formatDecimal(amount / 1_000, 1)
      : formatNumber(amount);
  const unit = isLarge ? "هزار تن" : isCompact ? "تن" : fa.balance.kg;
  const exactValue = `${formatNumber(amount)} ${fa.balance.kg}`;

  return (
    <div
      className={`tr-balance-plate-text ${className}`}
      style={{ left, top, width }}
      title={exactValue}
      aria-label={`${label}: ${exactValue}`}
    >
      <span className="tr-balance-plate-label">{label}</span>
      <bdi className="tr-balance-plate-value" dir="rtl">{displayValue}</bdi>
      <span className="tr-balance-plate-unit">{unit}</span>
    </div>
  );
}

function pointOnGauge(centerX, centerY, angle, radius) {
  const radians = (angle * Math.PI) / 180;
  return {
    x: centerX + radius * Math.sin(radians),
    y: centerY - radius * Math.cos(radians),
  };
}
